import React, { useState } from 'react';
import { auth } from '../lib/firebase';
import { signInWithCustomToken, setPersistence, browserLocalPersistence } from 'firebase/auth';

interface SimulationBannerProps {
  simulatedUser: { email: string; uid: string; displayName?: string };
}

export default function SimulationBanner({ simulatedUser }: SimulationBannerProps) {
  const [exiting, setExiting] = useState(false);

  const handleExit = async () => {
    if (exiting) return;
    setExiting(true);

    try {
      // 1. Read the admin's UID stored when simulation started
      const adminUid = sessionStorage.getItem('isats_admin_uid');

      if (adminUid) {
        // 2. Get the current simulated user's ID token to authorize the exit
        const token = await auth.currentUser?.getIdToken();

        // 3. Get a fresh admin token from the server, passing simulated user Bearer auth
        const res = await fetch('/api/admin/simulate-exit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {})
          },
          body: JSON.stringify({ adminUid }),
        });

        if (res.ok) {
          // 4. Clear simulation keys from sessionStorage
          sessionStorage.removeItem('isats_admin_uid');
          sessionStorage.removeItem('isats_admin_email');
          sessionStorage.removeItem('isats_sim_uid');
          sessionStorage.removeItem('isats_sim_email');

          // 5. Close simulation tab (or window) cleanly.
          // If the tab was opened via window.open, window.close() closes it cleanly without disturbing the main admin tab.
          window.close();
          // Fallback if browser blocks window.close() (e.g. non-popup tab)
          setTimeout(() => {
            window.location.href = '/';
          }, 300);
          return;
        }
      }

      // Fallback: clear sessionStorage and close/redirect
      sessionStorage.removeItem('isats_admin_uid');
      sessionStorage.removeItem('isats_admin_email');
      sessionStorage.removeItem('isats_sim_uid');
      sessionStorage.removeItem('isats_sim_email');
      window.close();
      setTimeout(() => { window.location.href = '/'; }, 300);
    } catch (err) {
      console.error('Exit simulation error:', err);
      sessionStorage.removeItem('isats_admin_uid');
      sessionStorage.removeItem('isats_admin_email');
      sessionStorage.removeItem('isats_sim_uid');
      sessionStorage.removeItem('isats_sim_email');
      window.close();
      setTimeout(() => { window.location.href = '/'; }, 300);
    }
  };

  return (
    <div className="sticky top-0 z-[9999] bg-amber-600 text-white px-4 py-2 flex items-center justify-between gap-4 text-sm shadow-lg">
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-white/20 shrink-0" title="Simulation Mode">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607zM10.5 7.5v6m3-3h-6" />
          </svg>
        </span>
        <span className="font-semibold whitespace-nowrap">Simulation Mode</span>
        <span className="hidden sm:inline text-amber-100 truncate">
          — Viewing as <strong>{simulatedUser.displayName || simulatedUser.email || simulatedUser.uid}</strong>
        </span>
        <span className="hidden sm:inline ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15 text-[11px] font-semibold">
          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Read Only
        </span>
      </div>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="shrink-0 px-3 py-1.5 bg-white text-amber-700 rounded-lg text-xs font-bold hover:bg-amber-50 transition-all cursor-pointer active:scale-[0.97] disabled:opacity-60"
      >
        {exiting ? 'Restoring session...' : 'Exit Simulation'}
      </button>
    </div>
  );
}
